import type { AttendeeType } from "@/hooks/useSignupFunnel";

export const ADULT_AGE = 18;

interface AgeGroupPolicy {
  attendees: AttendeeType[];
  /** Whether an adult signing themselves up must be 18+. */
  requiresAdultAge: boolean;
}

/**
 * Absent/blank = no age policy (permissive). Known values are allowlisted.
 * Anything else fails closed: no attendee types, so signup cannot proceed.
 */
function policyFor(ageGroup?: string | null): AgeGroupPolicy {
  if (ageGroup == null || ageGroup.trim() === "") {
    return { attendees: ["SELF", "CHILD"], requiresAdultAge: false };
  }
  switch (ageGroup.trim().toLowerCase()) {
    case "adult":
    case "adults":
      return { attendees: ["SELF"], requiresAdultAge: true };
    case "child":
    case "children":
      return { attendees: ["CHILD"], requiresAdultAge: false };
    case "mixed":
      return { attendees: ["SELF", "CHILD"], requiresAdultAge: true };
    default:
      return { attendees: [], requiresAdultAge: true };
  }
}

export function allowedAttendeeTypes(ageGroup?: string | null): AttendeeType[] {
  return policyFor(ageGroup).attendees;
}

export function isAttendeeTypeAllowed(
  ageGroup: string | null | undefined,
  attendeeType: AttendeeType,
): boolean {
  return allowedAttendeeTypes(ageGroup).includes(attendeeType);
}

export function requiresAdultSelfRegistration(
  ageGroup?: string | null,
): boolean {
  return policyFor(ageGroup).requiresAdultAge;
}

const TRUNCATED_DATE_OF_BIRTH = /^\d{4}(-(0[1-9]|1[0-2])(-99)?)?$/;
const ISO_CALENDAR_DATE = /^(\d{4})-(0[1-9]|1[0-2])-(0[1-9]|[12]\d|3[01])$/;

/**
 * True for a date of birth Spond stores *truncated* — a real record, but too
 * coarse to age-check. Spond's `TimeUtils.expandLocalDate` defines three such
 * forms: `yyyy`, `yyyy-MM`, and `yyyy-MM-99` (the profile screen masks the day
 * to `99` on every write). Anything unparseable is NOT truncated — it is no
 * usable record at all, and we ask for the date again.
 */
export function isTruncatedDateOfBirth(dateOfBirth: string): boolean {
  return TRUNCATED_DATE_OF_BIRTH.test(dateOfBirth.trim());
}

/** True for a real calendar `YYYY-MM-DD` (rejects truncated Spond forms). */
export function isIsoCalendarDate(value: string): boolean {
  const match = ISO_CALENDAR_DATE.exec(value.trim());
  if (!match) return false;
  const year = Number(match[1]);
  const month = Number(match[2]);
  const day = Number(match[3]);
  const dt = new Date(Date.UTC(year, month - 1, day));
  return (
    dt.getUTCFullYear() === year &&
    dt.getUTCMonth() === month - 1 &&
    dt.getUTCDate() === day
  );
}

function pad2(n: number): string {
  return String(n).padStart(2, "0");
}

/** Earliest/latest calendar days implied by a truncated Spond DOB. */
function truncatedDateBounds(
  truncated: string,
): { earliest: string; latest: string } | null {
  const value = truncated.trim();
  const dayMasked = /^(\d{4})-(0[1-9]|1[0-2])-99$/.exec(value);
  if (dayMasked) {
    const year = Number(dayMasked[1]);
    const month = Number(dayMasked[2]);
    const lastDay = new Date(Date.UTC(year, month, 0)).getUTCDate();
    return {
      earliest: `${dayMasked[1]}-${dayMasked[2]}-01`,
      latest: `${dayMasked[1]}-${dayMasked[2]}-${pad2(lastDay)}`,
    };
  }
  const yearMonth = /^(\d{4})-(0[1-9]|1[0-2])$/.exec(value);
  if (yearMonth) {
    const year = Number(yearMonth[1]);
    const month = Number(yearMonth[2]);
    const lastDay = new Date(Date.UTC(year, month, 0)).getUTCDate();
    return {
      earliest: `${yearMonth[1]}-${yearMonth[2]}-01`,
      latest: `${yearMonth[1]}-${yearMonth[2]}-${pad2(lastDay)}`,
    };
  }
  const yearOnly = /^(\d{4})$/.exec(value);
  if (yearOnly) {
    return {
      earliest: `${yearOnly[1]}-01-01`,
      latest: `${yearOnly[1]}-12-31`,
    };
  }
  return null;
}

export type TruncatedAdultAgeBound = "adult" | "minor" | "ambiguous";

/**
 * Whether a truncated recorded DOB can prove 18+ / under-18 without a day.
 * Ambiguous bounds require a full supplied `YYYY-MM-DD` on age-gated events.
 */
export function assessTruncatedAdultAge(
  truncated: string,
  now: Date = new Date(),
): TruncatedAdultAgeBound {
  const bounds = truncatedDateBounds(truncated);
  if (!bounds) return "ambiguous";
  const maxAge = ageInYearsAt(bounds.earliest, now);
  const minAge = ageInYearsAt(bounds.latest, now);
  if (maxAge !== null && maxAge < ADULT_AGE) return "minor";
  if (minAge !== null && minAge >= ADULT_AGE) return "adult";
  return "ambiguous";
}

export function ageInYearsAt(
  dateOfBirth: string,
  now: Date = new Date(),
): number | null {
  // Only full calendar dates are age-checkable. Partial/truncated forms are
  // handled by assessTruncatedAdultAge — `new Date("2010")` would silently
  // invent a day and mis-gate.
  if (!isIsoCalendarDate(dateOfBirth)) return null;

  const dob = new Date(dateOfBirth);
  if (Number.isNaN(dob.getTime()) || dob.getTime() > now.getTime()) {
    return null;
  }

  let age = now.getUTCFullYear() - dob.getUTCFullYear();
  const monthDelta = now.getUTCMonth() - dob.getUTCMonth();
  if (
    monthDelta < 0 ||
    (monthDelta === 0 && now.getUTCDate() < dob.getUTCDate())
  ) {
    age -= 1;
  }
  return age;
}

export type AgeVerdict = "ok" | "unknown" | "invalid" | "tooYoung" | "tooOld";

export function checkAttendeeAge(opts: {
  attendeeType: AttendeeType;
  dateOfBirth?: string | null;
  now?: Date;
}): AgeVerdict {
  if (!opts.dateOfBirth) return "unknown";

  const age = ageInYearsAt(opts.dateOfBirth, opts.now);
  if (age === null) return "invalid";

  if (opts.attendeeType === "SELF") {
    return age >= ADULT_AGE ? "ok" : "tooYoung";
  }
  return age < ADULT_AGE ? "ok" : "tooOld";
}
